// 자막 한 줄이 너무 길 때 어절(공백) 경계에서 시각 폭 기준으로 접는 순수 유틸.
// ★데이터가 아니라 "렌더 표시용"★ — segment.lines 배열 구조/음성 lineTimes 인덱스/직접수정 줄 인덱스를
//   전혀 건드리지 않는다. 접기는 최종 렌더 직전 문자열 하나 안에서 "\n" 삽입으로만 일어난다.
// 폭 근사: 한글 완성형은 거의 정폭(≈1em), 공백은 실측 ≈0.5em → 한글/일반 글자=1.0, 공백=0.5.
//   (canvas measureText 같은 정밀 측정은 자막 한 줄 접기엔 과설계라 가중치 근사로 충분.)

import { backOffBoundBreak } from "./korean-line-break";

/** 한 줄 목표 시각폭(글자 환산). LLM 자막 목표(≈12자) + 여유. 이 폭을 넘으면 어절 경계에서 접는다.
 *  16 = 따옴표 대사 한 줄("주말에 맛있는 거 먹으러 가요" = 시각폭 16)까지는 한 줄 허용, 더 길면 접기. */
export const MAX_VISUAL_WIDTH = 16;
/** 공백의 시각폭 가중치(한글 글자의 절반). */
export const SPACE_WEIGHT = 0.5;
/** 공백 외 일반 글자의 시각폭 가중치. */
export const CHAR_WEIGHT = 1.0;

/** 문자열의 시각 폭(글자 환산). 공백=0.5, 그 외=1.0. */
export function visualWidth(s: string): number {
  let w = 0;
  for (const ch of s ?? "") w += ch === " " ? SPACE_WEIGHT : CHAR_WEIGHT;
  return w;
}

/**
 * 한 줄을 어절(공백) 경계에서만 접어 maxWidth 이하 줄들로 만든 뒤 "\n" 으로 잇는다.
 * 렌더러는 whiteSpace:"pre-line" 으로 이 "\n" 을 실제 줄바꿈으로 표시한다.
 *
 * 규칙(회장님 원칙):
 *  - 정상 라인(<= maxWidth)은 무손실 통과(접지 않음).
 *  - 하드 트렁케이션 없음 — 어절 중간을 절대 자르지 않는다.
 *  - 단일 어절이 maxWidth 를 넘으면 그 어절은 자르지 말고 통째로 한 줄 유지(폭 초과 허용).
 */
export function wrapDisplayLine(line: string, maxWidth: number = MAX_VISUAL_WIDTH): string {
  const src = line ?? "";
  if (visualWidth(src) <= maxWidth) return src; // 정상 라인은 그대로

  const words = src.split(/\s+/).filter((w) => w.length > 0);
  const rows: string[] = [];
  let start = 0;
  while (start < words.length) {
    if (visualWidth(words[start]) > maxWidth) {
      rows.push(words[start]); // ★단일 어절이 캡 초과 → 자르지 않고 통째 유지★
      start += 1;
      continue;
    }
    let end = start + 1;
    while (end < words.length && visualWidth(words.slice(start, end + 1).join(" ")) <= maxWidth) end += 1;
    end = backOffBoundBreak(words, start, end); // ✂️ 붙은 말 사이면 한두 어절 내린다(2안 · 아래 wrapDisplayLineEm 주석)
    rows.push(words.slice(start, end).join(" "));
    start = end;
  }
  return rows.length ? rows.join("\n") : src;
}

/** 접힌 뒤 물리 줄 수(= "\n" 개수 + 1). 카드/레이아웃이 실제 표시 높이를 알아야 할 때 사용. */
export function displayRowCount(line: string, maxWidth: number = MAX_VISUAL_WIDTH): number {
  return wrapDisplayLine(line, maxWidth).split("\n").length;
}

// ─────────────────────────────────────────────────────────────
// ★가로 세이프존(px 폭) 기반 접기★ — body.safeWidth 가 있는 새 데이터 전용.
// 위의 wrapDisplayLine(고정 글자수 캡)은 safeWidth 없는 레거시 데이터의 폴백으로
// 그대로 유지한다(기존 영상 재현 불변). 여기부터는 "세이프존 폭 ÷ 현재 폰트 크기"로
// 용량이 동적으로 정해지므로, 폰트를 줄이면 한 줄에 더 들어가고 키우면 덜 들어간다.
// 폭 근사는 렌더 제목 래핑(StoryTemplate.wrapTitleLines)과 동일 계열의 글리프 폭 테이블.
// ─────────────────────────────────────────────────────────────

/** 한 글자의 폭(em 환산). 한글/CJK/전각 ≈1.0em, 공백 0.32, 대문자·숫자 0.58, 소문자 0.52, 그 외 0.5. */
export function glyphEm(ch: string): number {
  const c = ch.codePointAt(0) ?? 0;
  if ((c >= 0xac00 && c <= 0xd7a3) || (c >= 0x3130 && c <= 0x318f) || (c >= 0x4e00 && c <= 0x9fff) || (c >= 0xff00 && c <= 0xffef)) return 1.0;
  if (ch === " ") return 0.32;
  if (/[A-Z0-9]/.test(ch)) return 0.58;
  if (/[a-z]/.test(ch)) return 0.52;
  return 0.5;
}

/** 문자열 폭(em 환산) — glyphEm 합. */
export function visualEmWidth(s: string): number {
  let w = 0;
  for (const ch of s ?? "") w += glyphEm(ch);
  return w;
}

const SPACE_EM = 0.32;

/**
 * ★자유 줄(lineWrap:"free")의 하드 폭 한계(360 기준 px)★ — 직접수정 엔터/백스페이스로 사용자가
 * 구성한 줄은 세이프존 접기를 해제하되, 이 폭을 넘으면 그때만 접는다(미리보기 화면 이탈 방지).
 * 352 = 캔버스 360 에 근접(글리프 근사 과대측정 + letterSpacing −0.4/자로 실폭이 더 좁아 안전).
 * 렌더(StoryTemplate)와 직접수정 패널(병합 줄 폭 안내 계산)이 공유한다.
 */
export const FREE_LINE_HARD_WRAP_PX = 352;

/**
 * ★세이프존 접기★ — 한 줄을 어절(공백) 경계에서만 접어 각 줄이 maxEm(em) 이하가 되게 한 뒤
 * "\n" 으로 잇는다(렌더러 whiteSpace:"pre-line"). 규칙은 wrapDisplayLine 과 동일:
 *  - 정상 라인(≤ maxEm)은 무손실 통과. 하드 트렁케이션 없음(어절 중간 절대 안 자름).
 *  - 단일 어절이 maxEm 초과면 통째로 한 줄 유지(폭 초과 허용).
 * 배치 = ★순수 그리디(각 줄을 용량까지 최대 채움)★.
 *  ── 균형(min–max/꼬리/덜렁 한정) 배치를 전부 시도했으나 최종 폐기(회장님 결정 배경):
 *     균형은 본질적으로 "용량과 무관한 분할"이라, 발동되는 순간 폰트 스텝퍼를 여러 칸
 *     움직여도 화면이 안 변하는 죽은 구간이 생긴다(제보 2회 재현). 그리디는 1px 마다 어절이
 *     즉시 이동해 "폰트 조절 = 한 줄 글자 수 조절"이라는 사용자 모델과 일치한다.
 *     "마지막 어절 덜렁"은 이제 사용자가 스텝퍼 1~2칸(반응 보장) 또는 백스페이스 병합(free)으로
 *     직접 해소할 수 있으므로 시스템이 강제 교정하지 않는다.
 *  ── ✂️ 예외 하나(회장님 2026-09-23 «단어 잘림» · 2안 승인): 채운 끝이 ★붙은 말 사이★면
 *     («받을 수 / 있다» · «소개해 / 주세요» · 한 글자 말 «낸 / 보험료를») 그 한두 어절만 다음 줄로 내린다
 *     (korean-line-break.backOffBoundBreak). 균형 배치가 아니다 — 그 밖의 끊는 자리는 채우기 그대로라
 *     스텝퍼는 계속 반응하고, 붙은 말이 걸린 스텝에서만 가끔 한 칸 모양이 안 바뀔 수 있다(승인된 대가).
 */
export function wrapDisplayLineEm(line: string, maxEm: number): string {
  const src = line ?? "";
  if (!(maxEm > 0)) return src;
  if (visualEmWidth(src) <= maxEm) return src; // 정상 라인은 그대로

  const words = src.split(/\s+/).filter((w) => w.length > 0);
  if (words.length <= 1) return src; // 단일 어절 초과 → 통째 유지
  const wordEms = words.map(visualEmWidth);

  // 1) 그리디 채움 — 각 줄을 용량까지 최대로(스텝마다 즉시 반응). 단일 어절 초과는 자기 줄 유지.
  //    ✂️ 채운 끝이 붙은 말 사이면 그 말만 다음 줄로(위 주석의 예외 하나).
  const rows: string[] = [];
  let start = 0;
  while (start < words.length) {
    let end = start + 1;
    let em = wordEms[start];
    while (end < words.length && em + SPACE_EM + wordEms[end] <= maxEm) {
      em += SPACE_EM + wordEms[end];
      end += 1;
    }
    end = backOffBoundBreak(words, start, end);
    rows.push(words.slice(start, end).join(" "));
    start = end;
  }
  if (rows.length <= 1) return src;
  return rows.join("\n");
}

/**
 * ★자유 줄(lineWrap:"free") 전용 — 글자 단위 접기(단어 잘림 허용)★.
 * 사용자가 엔터/백스페이스로 직접 구성한 줄은 어절 보호(단어잘림 방지)까지 해제한다 —
 * "유저가 어차피 컨트롤하는 거니까 시스템에서 막지 말라"(회장님). 시스템이 지키는 건
 * 화면 폭 하나: maxEm 에 닿으면 그 글자에서 정확히 접는다(텍스트 에디터 줄바꿈과 동일).
 *  - 어절 경계 무시 — 단어 중간에서도 접힌다(의도된 동작).
 *  - 새 줄 첫머리의 공백은 버린다(에디터 관행).
 *  - 단일 초장어절도 글자 단위로 접히므로 화면 폭을 절대 벗어나지 않는다(어절 접기와 달리
 *    폭 초과 허용 예외가 없다).
 */
export function wrapDisplayLineEmChars(line: string, maxEm: number): string {
  const src = line ?? "";
  if (!(maxEm > 0)) return src;
  if (visualEmWidth(src) <= maxEm) return src; // 정상 라인은 그대로

  const rows: string[] = [];
  let cur = "";
  let curEm = 0;
  for (const ch of src) {
    const w = glyphEm(ch);
    if (cur.length > 0 && curEm + w > maxEm) {
      rows.push(cur);
      if (ch === " ") { // 줄바꿈 지점의 공백은 새 줄 첫머리에 남기지 않는다
        cur = "";
        curEm = 0;
        continue;
      }
      cur = ch;
      curEm = w;
    } else {
      cur += ch;
      curEm += w;
    }
  }
  if (cur.length) rows.push(cur);
  // 줄 끝 공백 제거(중앙정렬 미세 틀어짐 방지) + 공백뿐인 줄 제거.
  const cleaned = rows.map((r) => r.replace(/\s+$/, "")).filter((r) => r.length > 0);
  return cleaned.length ? cleaned.join("\n") : src;
}
